import type { Components } from "@mui/material/styles";

import type { Theme } from "../types";

function getContainedVars(color: string): Record<string, string> {
	return {
		"--Button-containedBg": `var(--mui-palette-${color}-main)`,
		"--Button-containedHoverBg": `var(--mui-palette-${color}-hovered)`,
		"--Button-containedActiveBg": `var(--mui-palette-${color}-activated)`,
	};
}

function getOutlinedVars(color: string, dark: boolean): Record<string, string> {
	const vars = {
		"--Button-outlinedBorder": `var(--mui-palette-${color}-main)`,
		"--Button-outlinedHoverBg": `var(--mui-palette-${color}-main)`,
		"--Button-outlinedActiveBg": `var(--mui-palette-${color}-activated)`,
		"--Button-outlinedHoverText": `var(--mui-palette-${color}-contrastText)`,
	};

	// Secondary outlined uses lighter/darker border shades (200 light, 700 dark)
	// instead of main, because secondary.main is too dark for a subtle outline in light mode
	// and too light in dark mode — matching the design tokens' intent for secondary surfaces.
	if (color === "secondary") {
		vars["--Button-outlinedBorder"] = dark
			? "var(--mui-palette-secondary-700)"
			: "var(--mui-palette-secondary-200)";
	}

	return vars;
}

function getTextVars(color: string): Record<string, string> {
	return {
		"--Button-textHoverBg": `var(--mui-palette-${color}-hovered)`,
		"--Button-textActiveBg": `var(--mui-palette-${color}-activated)`,
	};
}

export const MuiButton = {
	defaultProps: { disableRipple: true },
	styleOverrides: {
		root: ({ theme }) => ({
			borderRadius: "8px",
			minHeight: "var(--Button-minHeight)",
			minWidth: "unset",
			textTransform: "none",
			"&:focus-visible": {
				outline: "2px solid var(--mui-palette-primary-main)",
			},
			"&.Mui-disabled": {
				opacity: 0.38,
				cursor: "not-allowed",
				pointerEvents: "auto", // Allow cursor change
				color: theme.palette.text.disabled,
				"&:hover": {
					// Remove all hover effects on disabled buttons
					backgroundColor: "transparent",
					boxShadow: "none",
				},
			},
		}),
		text: ({ theme }) => ({
			"&:hover": { backgroundColor: "var(--Button-textHoverBg)" },
			"&:active": { backgroundColor: "var(--Button-textActiveBg)" },
			"&.Mui-disabled": {
				backgroundColor: "transparent",
				color: theme.palette.text.disabled,
				"&:hover": {
					backgroundColor: "transparent",
				},
			},
		}),
		textPrimary: getTextVars("primary"),
		textSecondary: getTextVars("secondary"),
		textSuccess: getTextVars("success"),
		textInfo: getTextVars("info"),
		textWarning: getTextVars("warning"),
		textError: getTextVars("error"),
		outlined: ({ theme }) => ({
			boxShadow: "none",
			borderColor: "var(--Button-outlinedBorder)",
			transition:
				"background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
			"&:hover": {
				borderColor: "var(--Button-outlinedBorder)",
				backgroundColor: "var(--Button-outlinedHoverBg)",
				color: "var(--Button-outlinedHoverText)",
			},
			"&:active": {
				backgroundColor: "var(--Button-outlinedActiveBg)",
				color: "var(--Button-outlinedHoverText)",
			},
			"&.Mui-disabled": {
				backgroundColor: "transparent",
				borderColor: theme.palette.text.disabled,
				boxShadow: "none",
				color: theme.palette.text.disabled,
				"&:hover": {
					backgroundColor: "transparent",
					borderColor: theme.palette.text.disabled,
					boxShadow: "none",
				},
			},
		}),
		outlinedPrimary: ({ theme }) => {
			return getOutlinedVars("primary", theme.palette.mode === "dark");
		},
		outlinedSecondary: ({ theme }) => {
			return getOutlinedVars("secondary", theme.palette.mode === "dark");
		},
		outlinedSuccess: ({ theme }) => {
			return getOutlinedVars("success", theme.palette.mode === "dark");
		},
		outlinedInfo: ({ theme }) => {
			return getOutlinedVars("info", theme.palette.mode === "dark");
		},
		outlinedWarning: ({ theme }) => {
			return getOutlinedVars("warning", theme.palette.mode === "dark");
		},
		outlinedError: ({ theme }) => {
			return getOutlinedVars("error", theme.palette.mode === "dark");
		},
		contained: ({ theme }) => ({
			backgroundColor: "var(--Button-containedBg)",
			boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.08)",
			transition: "background-color 0.15s ease",
			"&:hover": {
				backgroundColor: "var(--Button-containedHoverBg)",
			},
			"&:active": {
				backgroundColor: "var(--Button-containedActiveBg)",
				boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.1)",
			},
			"&:focus-visible": {
				boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.15)",
				outlineOffset: "1px",
			},
			"&.Mui-disabled": {
				backgroundColor:
					theme.palette.action.disabledBackground || theme.palette.grey[100],
				boxShadow: "none",
				color: theme.palette.text.disabled,
				"&:hover": {
					backgroundColor:
						theme.palette.action.disabledBackground || theme.palette.grey[100],
					boxShadow: "none",
				},
			},
		}),
		containedPrimary: getContainedVars("primary"),
		containedSecondary: getContainedVars("secondary"),
		containedSuccess: getContainedVars("success"),
		containedInfo: getContainedVars("info"),
		containedWarning: getContainedVars("warning"),
		containedError: getContainedVars("error"),
		sizeSmall: {
			"--Button-minHeight": "28px",
			fontSize: "0.75rem",
			lineHeight: "20px",
		},
		sizeMedium: {
			"--Button-minHeight": "32px",
			fontSize: "0.8125rem",
			lineHeight: "24px",
		},
		sizeLarge: {
			"--Button-minHeight": "40px",
			fontSize: "0.875rem",
			lineHeight: "28px",
		},
	},
} satisfies Components<Theme>["MuiButton"];
